import React, { useEffect, useState } from "react";
import { View, ScrollView, StyleSheet, Alert, TouchableOpacity } from "react-native";
import { useLocalSearchParams, router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import ThemedInput from "@/components/ThemedInput";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { getEntityPermissions } from "@/lib/permissions";
import { locationService, LocationBannedUserType } from "@/services/locationService";
import { userService } from "@/services/userService";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { UserPicker } from "@/components/Pickers/UserPicker";
import { MarketPicker } from "@/components/Pickers/MarketPicker";
import { LocationType } from "@/types/db/locations";
import { supabase } from "@/lib/supabase-client";

// Location data interface
export interface LocationData extends LocationType {
  banned_users?: LocationBannedUserType[];
}

export default function LocationEditPage() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors } = useTheme();
  const { userRoles, tenantId } = useAuth();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [editedLocation, setEditedLocation] = useState<LocationData | null>(null);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "banned_users">("overview");

  const [bannedUsers, setBannedUsers] = useState<any[]>([]);
  const [bannedUserReasons, setBannedUserReasons] = useState<{ [userId: string]: string }>({});
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [showMarketPicker, setShowMarketPicker] = useState(false);
  const [marketName, setMarketName] = useState<string | null>(null);

  const permissions = getEntityPermissions(userRoles, "locations");

  // Load location data
  useEffect(() => {
    if (id) {
      loadLocationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadLocationData = async () => {
    try {
      setLoading(true);
      const location = await locationService.getLocationById(id!);
      setLocationData(location);
      setEditedLocation(location);

      // Load market name if market_id exists
      if ((location as any).market_id) {
        const { data: market } = await supabase
          .schema("lists")
          .from("markets")
          .select("name")
          .eq("id", (location as any).market_id)
          .single();
        setMarketName(market?.name || null);
      } else {
        setMarketName(null);
      }

      // Load banned users from locations_banned_users link table
      const bannedUserIds = (location.banned_users || []).map((bu) => bu.user_id);
      if (bannedUserIds.length > 0) {
        const locationBannedUsers = await userService.getUsers({ userIds: bannedUserIds });
        setBannedUsers(locationBannedUsers);

        // Load reasons
        const reasons: { [userId: string]: string } = {};
        location.banned_users?.forEach((bu) => {
          if (bu.reason) {
            reasons[bu.user_id] = bu.reason;
          }
        });
        setBannedUserReasons(reasons);
      } else {
        setBannedUsers([]);
        setBannedUserReasons({});
      }
    } catch (error: any) {
      toast.showToast({
        title: "Failed to load location",
        message: error?.message || "Unable to load location data",
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editedLocation || !id || !tenantId) return;

    setSaving(true);
    try {
      // Save location data (name, address, etc.) - strip out banned_users relation
      const { banned_users, ...locationDataToSave } = editedLocation;
      await locationService.saveRowPatches([locationDataToSave]);

      // Sync banned users in link table
      const bannedUserIds = bannedUsers.map((u) => u.id!);
      await locationService.syncLocationBannedUsers(id, tenantId, bannedUserIds, bannedUserReasons);

      setLocationData(editedLocation);
      setEditing(false);

      toast.showToast({
        title: "Location updated",
        message: "Location has been successfully updated",
        toastType: "success",
      });

      await loadLocationData();
    } catch (error: any) {
      toast.showToast({
        title: "Update failed",
        message: error?.message || "Failed to update location",
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedLocation(locationData);
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!locationData?.id) return;

    Alert.alert(
      "Delete Location",
      `Are you sure you want to delete "${locationData.name}"? This action cannot be undone.`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            try {
              await locationService.deleteLocationsByIds([locationData.id]);
              toast.showToast({
                title: "Location deleted",
                message: "Location has been successfully deleted",
                toastType: "success",
              });
              router.push("/(app)/settings/locations");
            } catch (error: any) {
              toast.showToast({
                title: "Delete failed",
                message: error?.message || "Failed to delete location",
                toastType: "error",
              });
            }
          },
        },
      ],
    );
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.backgroundSecondary }]}>
        <ThemedText>Loading location data...</ThemedText>
      </View>
    );
  }

  if (!locationData) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.backgroundSecondary }]}>
        <MaterialCommunityIcons name="alert-circle-outline" size={48} color={colors.dangerText} />
        <ThemedText style={{ marginTop: 12 }}>Location not found</ThemedText>
        <Button text="Go Back" onPress={() => router.push("/(app)/settings/locations")} style={{ marginTop: 16 }} />
      </View>
    );
  }

  const bannedUsersCount = bannedUsers.length;

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <TouchableOpacity onPress={() => router.push("/(app)/settings/locations")} style={styles.backButton}>
              <MaterialCommunityIcons name="arrow-left" size={24} color={colors.text} />
            </TouchableOpacity>
            <View style={styles.headerInfo}>
              <ThemedText type="title">{locationData.name}</ThemedText>
              <ThemedText style={{ opacity: 0.7, marginTop: 4, fontSize: 13 }}>
                {locationData.street}, {locationData.city}, {locationData.state} {locationData.zip}
              </ThemedText>
            </View>
          </View>

          <View style={styles.headerActions}>
            {editing ? (
              <>
                <Button
                  text="Cancel"
                  onPress={handleCancel}
                  style={[styles.actionButton, { backgroundColor: "transparent", borderColor: colors.border }]}
                  textStyle={{ color: colors.text }}
                />
                <Button
                  text={saving ? "Saving..." : "Save"}
                  onPress={handleSave}
                  disabled={saving}
                  style={[styles.actionButton, { backgroundColor: colors.primary }]}
                  textStyle={{ color: "white" }}
                />
              </>
            ) : (
              <>
                {permissions.update && (
                  <Button
                    text="Edit"
                    iconPosition="right"
                    icon="pencil"
                    onPress={() => setEditing(true)}
                    style={[styles.actionButton, { backgroundColor: colors.primary }]}
                    textStyle={{ color: "white" }}
                  />
                )}
                {permissions.delete && (
                  <Button
                    text="Delete"
                    icon="delete"
                    iconPosition="right"
                    onPress={handleDelete}
                    style={[styles.actionButton, { backgroundColor: colors.red.background }]}
                    textStyle={{ color: "white" }}
                  />
                )}
              </>
            )}
          </View>
        </View>
      </View>

      {/* Tab Navigation */}
      <View style={[styles.tabNav, { borderBottomColor: colors.border }]}>
        {[
          { key: "overview", label: "Overview" },
          { key: "banned_users", label: `Banned Users (${bannedUsersCount})` },
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, activeTab === tab.key && { borderBottomColor: colors.primary }]}
            onPress={() => setActiveTab(tab.key as any)}
          >
            <ThemedText style={[styles.tabText, activeTab === tab.key && { color: colors.primary }]}>
              {tab.label}
            </ThemedText>
          </TouchableOpacity>
        ))}
      </View>

      {/* Tab Content */}
      <ScrollView style={styles.tabContent}>
        {activeTab === "overview" && (
          <View style={styles.section}>
            {editing && editedLocation ? (
              <>
                <View style={styles.inputGroup}>
                  <ThemedText style={styles.label}>Name *</ThemedText>
                  <ThemedInput
                    value={editedLocation.name}
                    onChangeText={(text) => setEditedLocation({ ...editedLocation, name: text })}
                    style={styles.input}
                  />
                </View>
                <View style={styles.inputGroup}>
                  <ThemedText style={styles.label}>Description</ThemedText>
                  <ThemedInput
                    value={editedLocation.description || ""}
                    onChangeText={(text) => setEditedLocation({ ...editedLocation, description: text })}
                    multiline
                    numberOfLines={3}
                    style={styles.input}
                  />
                </View>
                <View style={styles.inputGroup}>
                  <ThemedText style={styles.label}>Street *</ThemedText>
                  <ThemedInput
                    value={editedLocation.street}
                    onChangeText={(text) => setEditedLocation({ ...editedLocation, street: text })}
                    style={styles.input}
                  />
                </View>
                <View style={styles.row}>
                  <View style={[styles.inputGroup, { flex: 1 }]}>
                    <ThemedText style={styles.label}>City *</ThemedText>
                    <ThemedInput
                      value={editedLocation.city}
                      onChangeText={(text) => setEditedLocation({ ...editedLocation, city: text })}
                      style={styles.input}
                    />
                  </View>
                  <View style={[styles.inputGroup, { width: 80 }]}>
                    <ThemedText style={styles.label}>State *</ThemedText>
                    <ThemedInput
                      value={editedLocation.state}
                      onChangeText={(text) => setEditedLocation({ ...editedLocation, state: text.toUpperCase() })}
                      maxLength={2}
                      style={styles.input}
                    />
                  </View>
                  <View style={[styles.inputGroup, { width: 100 }]}>
                    <ThemedText style={styles.label}>ZIP *</ThemedText>
                    <ThemedInput
                      value={editedLocation.zip}
                      onChangeText={(text) => setEditedLocation({ ...editedLocation, zip: text })}
                      keyboardType="numeric"
                      style={styles.input}
                    />
                  </View>
                </View>
                <View style={styles.inputGroup}>
                  <ThemedText style={styles.label}>Instructions</ThemedText>
                  <ThemedInput
                    value={editedLocation.instructions || ""}
                    onChangeText={(text) => setEditedLocation({ ...editedLocation, instructions: text })}
                    multiline
                    numberOfLines={3}
                    style={styles.input}
                  />
                </View>
                <View style={styles.inputGroup}>
                  <ThemedText style={styles.label}>Market</ThemedText>
                  <TouchableOpacity
                    style={[styles.pickerTrigger, { borderColor: colors.border }]}
                    onPress={() => setShowMarketPicker(true)}
                  >
                    <ThemedText style={marketName ? {} : { color: colors.placeholder }}>
                      {marketName || "Select a market..."}
                    </ThemedText>
                    {(editedLocation as any).market_id && (
                      <TouchableOpacity
                        onPress={() => {
                          setEditedLocation({ ...editedLocation, market_id: null } as any);
                          setMarketName(null);
                        }}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      >
                        <MaterialCommunityIcons name="close-circle" size={18} color={colors.textSecondary} />
                      </TouchableOpacity>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            ) : (
              <>
                <View style={styles.infoRow}>
                  <ThemedText style={styles.infoLabel}>Name</ThemedText>
                  <ThemedText>{locationData.name}</ThemedText>
                </View>
                {locationData.description && (
                  <View style={styles.infoRow}>
                    <ThemedText style={styles.infoLabel}>Description</ThemedText>
                    <ThemedText>{locationData.description}</ThemedText>
                  </View>
                )}
                <View style={styles.infoRow}>
                  <ThemedText style={styles.infoLabel}>Address</ThemedText>
                  <ThemedText>
                    {locationData.street}, {locationData.city}, {locationData.state} {locationData.zip}
                  </ThemedText>
                </View>
                {locationData.instructions && (
                  <View style={styles.infoRow}>
                    <ThemedText style={styles.infoLabel}>Instructions</ThemedText>
                    <ThemedText>{locationData.instructions}</ThemedText>
                  </View>
                )}
                <View style={styles.infoRow}>
                  <ThemedText style={styles.infoLabel}>Market</ThemedText>
                  <ThemedText>{marketName || "—"}</ThemedText>
                </View>
                <View style={styles.infoRow}>
                  <ThemedText style={styles.infoLabel}>Banned Users</ThemedText>
                  <ThemedText>{bannedUsersCount} users</ThemedText>
                </View>
              </>
            )}
          </View>
        )}

        {activeTab === "banned_users" && (
          <View style={styles.section}>
            {editing && (
              <Button
                text="Select Users to Ban"
                onPress={() => setShowUserPicker(true)}
                style={styles.pickerButton}
                icon="account-cancel"
              />
            )}
            {bannedUsers.length > 0 ? (
              <View style={styles.userList}>
                {bannedUsers.map((user) => {
                  const displayName = user.meta_data?.preferred_name || user.first_name;
                  const fullName = `${displayName} ${user.last_name}`.trim();

                  return (
                    <View key={user.id} style={[styles.userItem, { borderBottomColor: colors.border }]}>
                      <View style={styles.userInfo}>
                        <ThemedText type="defaultSemiBold">{fullName || "Unknown User"}</ThemedText>
                        {user.email_address && <ThemedText style={styles.userDetail}>{user.email_address}</ThemedText>}
                        {editing ? (
                          <ThemedInput
                            value={bannedUserReasons[user.id!] || ""}
                            onChangeText={(text) => setBannedUserReasons({ ...bannedUserReasons, [user.id!]: text })}
                            placeholder="Reason for ban (optional)"
                            style={{ marginTop: 8 }}
                          />
                        ) : (
                          bannedUserReasons[user.id!] && (
                            <ThemedText style={styles.banReason}>Reason: {bannedUserReasons[user.id!]}</ThemedText>
                          )
                        )}
                      </View>
                      {editing && (
                        <TouchableOpacity
                          onPress={() => {
                            const updatedUsers = bannedUsers.filter((u) => u.id !== user.id);
                            setBannedUsers(updatedUsers);
                            const newReasons = { ...bannedUserReasons };
                            delete newReasons[user.id!];
                            setBannedUserReasons(newReasons);
                          }}
                          style={styles.removeButton}
                        >
                          <MaterialCommunityIcons name="close-circle" size={24} color={colors.dangerText} />
                        </TouchableOpacity>
                      )}
                    </View>
                  );
                })}
              </View>
            ) : (
              <ThemedText style={{ opacity: 0.5 }}>No banned users</ThemedText>
            )}
          </View>
        )}
      </ScrollView>

      {/* User Picker Modal */}
      {showUserPicker && (
        <View style={[styles.modalOverlay, { backgroundColor: colors.background + "CC" }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
            <UserPicker
              multiple
              selectedUserIds={bannedUsers.map((u) => u.id!)}
              onMultiPick={(selectedUsers) => {
                setBannedUsers(selectedUsers);
                setShowUserPicker(false);
              }}
              onCancel={() => setShowUserPicker(false)}
            />
          </View>
        </View>
      )}

      {/* Market Picker Modal */}
      {showMarketPicker && (
        <View style={[styles.modalOverlay, { backgroundColor: colors.background + "CC" }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
            <MarketPicker
              onPick={async (marketId) => {
                if (editedLocation) {
                  setEditedLocation({ ...editedLocation, market_id: marketId } as any);
                  // Fetch market name for display
                  const { data: market } = await supabase
                    .schema("lists")
                    .from("markets")
                    .select("name")
                    .eq("id", marketId)
                    .single();
                  setMarketName(market?.name || null);
                }
                setShowMarketPicker(false);
              }}
              onCancel={() => setShowMarketPicker(false)}
            />
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  header: {
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  backButton: {
    marginRight: 12,
    padding: 4,
  },
  headerInfo: {
    flex: 1,
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
  },
  actionButton: {
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  tabNav: {
    flexDirection: "row",
    borderBottomWidth: 1,
  },
  tab: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  tabText: {
    fontSize: 14,
    fontWeight: "500",
  },
  tabContent: {
    flex: 1,
  },
  section: {
    padding: 16,
    gap: 16,
  },
  inputGroup: {
    gap: 8,
  },
  row: {
    flexDirection: "row",
    gap: 12,
  },
  label: {
    fontSize: 14,
    opacity: 0.7,
    fontWeight: "500",
  },
  input: {
    marginBottom: 0,
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    alignItems: "center",
  },
  infoLabel: {
    opacity: 0.7,
    flex: 1,
  },
  pickerButton: {
    alignSelf: "flex-start",
    marginBottom: 16,
  },
  pickerTrigger: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderRadius: 8,
  },
  userList: {
    gap: 0,
  },
  userItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
  },
  userInfo: {
    flex: 1,
    gap: 2,
  },
  userDetail: {
    fontSize: 12,
    opacity: 0.6,
  },
  banReason: {
    fontSize: 11,
    opacity: 0.7,
    fontStyle: "italic",
    marginTop: 4,
  },
  removeButton: {
    padding: 4,
  },
  modalOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    width: "90%",
    maxWidth: 500,
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
});
